/**
 * Pure publish-state model — shared by the per-asset pill and (later) the project
 * footer. No React, no fetching: maps a git status snapshot to a discrete state,
 * and maps state/outcome to user-facing copy that respects the Standard/Advanced
 * view mode (Standard never leaks git jargon). Headless model + copy → lives in
 * `lib/` (peer to scope-filter / color-palette), not in a components dir.
 */

import { t } from '@lingui/core/macro';
import type { PullKind, PushKind } from '@sdk';
import { ViewMode } from '@src/components/view-mode';

export type PublishState = 'no-repo' | 'local-only' | 'aligned' | 'unpublished';

// Typed push outcome — the SDK's mirror of `PushKind` in git_repo.py.
// Re-exported for existing importers of this module.
export type { PushKind } from '@sdk';

export interface PublishStatusInput {
  hasRepo: boolean;
  /** Commits ahead of the remote for this scope (per-file for assets). */
  unpushed: number;
  /** Working-tree changes — footer scope only; assets auto-commit so omit. */
  uncommitted?: number;
  /** Whether a tracking remote exists. Footer passes it; assets omit. */
  hasUpstream?: boolean;
}

export interface PublishStatus {
  state: PublishState;
  pendingCount: number;
}

export function derivePublishState(input: PublishStatusInput): PublishStatus {
  if (!input.hasRepo) {
    return { state: 'no-repo', pendingCount: 0 };
  }
  const pending = input.unpushed + (input.uncommitted ?? 0);
  if (pending > 0) {
    return { state: 'unpublished', pendingCount: pending };
  }
  // Nothing pending: distinguish a remote-tracked-and-current branch from a
  // local-only repo (no remote). Only the footer passes hasUpstream; for assets
  // it's undefined and both render identically (just the version, no Publish).
  const state: PublishState = input.hasUpstream === false ? 'local-only' : 'aligned';
  return { state, pendingCount: 0 };
}

// Advanced copy is for power-user tiers only. Vibe (simpler than Standard) and
// Standard both get the plain-language copy — so test for the advanced tiers
// explicitly rather than "!== Standard" (which would wrongly catch Vibe).
const isAdvanced = (mode: ViewMode): boolean => mode === ViewMode.Advanced || mode === ViewMode.Dev;

export interface PublishLabels {
  /** Button label for the publish action. */
  publishLabel: string;
  /** Tooltip on the publish affordance. */
  publishTitle: string;
  /** Whether to show the raw pending count next to Publish (Advanced only). */
  showCount: boolean;
}

/** Copy for the pill's Publish affordance, by state + mode. */
export function publishCopy(state: PublishState, mode: ViewMode): PublishLabels {
  const advanced = isAdvanced(mode);
  return {
    publishLabel: 'Publish',
    publishTitle: advanced ? 'Publish your changes to the remote' : 'Publish your changes',
    showCount: advanced,
  };
}

/** Which way a one-click sync went: Publish (push), Pull, or bringing a shared commit in (cherry-pick). */
export type GitSyncOp = 'push' | 'pull' | 'cherry-pick';

export interface GitOutcomeToast {
  level: 'success' | 'error';
  title: string;
  message: string;
  /** Offer the conflict-resolver agent (`git.resolve-conflict`). */
  resolvable: boolean;
}

type CopyOpts = { branch?: string | null; message?: string };

/**
 * Plain-language toast for a push or pull outcome. Standard mode contains no
 * git terms; Advanced may include branch/remote detail.
 *
 * A conflict is ONE case for both directions: a push and a pull leave the same
 * stuck tree, so they say the same thing and both offer Resolve — in every
 * view, since switching views is no way out of a half-finished rebase.
 */
export function gitOutcomeCopy(
  op: GitSyncOp,
  kind: PushKind | PullKind,
  mode: ViewMode,
  opts: CopyOpts = {},
): GitOutcomeToast {
  if (kind === 'conflict') return conflictCopy(mode, opts);
  return op === 'push' ? pushCopy(kind as PushKind, mode, opts) : pullCopy(kind as PullKind, mode, opts);
}

function conflictCopy(mode: ViewMode, opts: CopyOpts): GitOutcomeToast {
  const raw = opts.message?.trim() || '';
  return isAdvanced(mode)
    ? { level: 'error', title: t`Merge conflict`, message: raw || 'A rebase conflict is in progress.', resolvable: true }
    : {
        level: 'error',
        title: t`Changes need merging`,
        message: t`Someone else changed the same thing. Let the assistant merge the two versions.`,
        resolvable: true,
      };
}

function pushCopy(kind: PushKind, mode: ViewMode, opts: CopyOpts): GitOutcomeToast {
  const advanced = isAdvanced(mode);
  const raw = opts.message?.trim() || '';
  switch (kind) {
    case 'pushed':
      return {
        level: 'success',
        title: t`Published`,
        message: advanced && opts.branch ? `Pushed to ${opts.branch}.` : 'Your changes are now published.',
        resolvable: false,
      };
    case 'nothing':
      return {
        level: 'success',
        title: t`Nothing to publish`,
        message: t`Everything is already up to date.`,
        resolvable: false,
      };
    case 'permission':
      return {
        level: 'error',
        title: advanced ? 'Push rejected — no access' : "Can't publish here",
        message: advanced
          ? raw || 'You do not have write access to this remote.'
          : "You don't have access to publish to this location.",
        resolvable: false,
      };
    case 'no_remote':
      return {
        level: 'error',
        title: advanced ? 'No remote configured' : 'Nowhere to publish yet',
        message: advanced
          ? raw || 'No git remote/upstream is configured for this branch.'
          : "This project isn't connected to a place to publish to.",
        resolvable: false,
      };
    case 'network':
      return {
        level: 'error',
        title: t`Couldn't reach the server`,
        message: t`Check your connection and try again.`,
        resolvable: false,
      };
    case 'no_repo':
      return {
        level: 'error',
        title: t`Not set up for publishing`,
        message: t`This file is not in a versioned project.`,
        resolvable: false,
      };
    default:
      return {
        level: 'error',
        title: advanced ? 'Push failed' : "Couldn't publish",
        message: advanced ? raw || 'Push failed.' : 'Something went wrong. Please try again.',
        resolvable: false,
      };
  }
}

/** The inbound twin of ``pushCopy``: Standard says "updates from the cloud", Advanced says pull. */
function pullCopy(kind: PullKind, mode: ViewMode, opts: CopyOpts): GitOutcomeToast {
  const advanced = isAdvanced(mode);
  const raw = opts.message?.trim() || '';
  switch (kind) {
    case 'pulled':
      return {
        level: 'success',
        title: t`Updated`,
        message:
          advanced && opts.branch ? `Pulled ${opts.branch}.` : t`You now have the latest changes from the cloud.`,
        resolvable: false,
      };
    case 'picked':
      return {
        level: 'success',
        title: t`Brought in`,
        message: t`The shared changes are now in your project.`,
        resolvable: false,
      };
    case 'nothing':
      return {
        level: 'success',
        title: t`Already up to date`,
        message: t`There is nothing new in the cloud.`,
        resolvable: false,
      };
    case 'network':
      return {
        level: 'error',
        title: t`Couldn't reach the server`,
        message: t`Check your connection and try again.`,
        resolvable: false,
      };
    case 'permission':
    case 'no_remote':
    case 'no_repo':
    default:
      return {
        level: 'error',
        title: advanced ? 'Pull failed' : t`Couldn't update`,
        message: advanced ? raw || 'Pull failed.' : t`Something went wrong. Please try again.`,
        resolvable: false,
      };
  }
}
